import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderSelector } from './FolderSelector';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { MoveFolderConfirmModal } from './MoveFolderConfirmModal';

type Props = {
  isOpen: boolean;
  onClose(): void;
  folder: Folder;
};

export const MoveFolderModal: React.FC<Props> = ({ isOpen, onClose, folder }: Props) => {
  const { t } = useTranslation();
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

  const handleClose = useCallback(() => {
    setSelectedFolder(null);
    setProcessing(false);
    onClose();
  }, [onClose]);

  const [processing, setProcessing] = useState(false);

  const handleMove = useCallback(async () => {
    if (!selectedFolder) return;
    setProcessing(true);
  }, [selectedFolder]);

  return (
    <>
      <GeneralModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('workspace.folder.move.label')}
        submitButton={
          <Button onClick={handleMove} disabled={!selectedFolder} loading={processing}>
            {t('workspace.modal.move')}
          </Button>
        }
        cancelButton={
          <Button onClick={handleClose} color="secondary">
            {t('common.action.cancel')}
          </Button>
        }
      >
        <div className="p-2" style={{ width: '600px' }}>
          {t('workspace.moveFolder.description', { name: folder.name })}
          <hr className="mt-2" />
          {isOpen && (
            <FolderSelector
              workspaceId={folder.workspaceId}
              itemId={folder.id}
              onChangeSelectedFolder={setSelectedFolder}
            />
          )}
        </div>
      </GeneralModal>

      {selectedFolder && processing && (
        <MoveFolderConfirmModal
          isOpen={true}
          onClose={(moved) => {
            setProcessing(false);
            moved && onClose();
          }}
          folder={folder}
          destinationFolder={selectedFolder}
        />
      )}
    </>
  );
};
